import React, { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Button, Tooltip } from '@material-ui/core';

import { WorkflowDto } from '../Workflow.interface';
import {
  useUpdateWorkflow,
  useRevertWorkflow,
  useDeleteWorkflow
} from '../Workflow.hooks';
import ElectronicSignatureDialog from '../../electronic-signature/ElectronicSignatureDialog';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';

export interface WorkflowStatusActionsProps {
  workflow: WorkflowDto;
  onChange?: (workflow: WorkflowDto) => void;
}

const WorkflowStatusActions: React.FC<WorkflowStatusActionsProps> = ({
  workflow,
  onChange
}) => {
  const history = useHistory();
  const { id, name, status, version } = workflow;
  const [openSignatureDialog, setOpenSignatureDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openRevertDialog, setOpenRevertDialog] = useState(false);
  const { updatedWorkflow, updateWorkflow } = useUpdateWorkflow(id);
  const { workflow: revertedWorkflow, revert } = useRevertWorkflow(id);
  const { deleteWorkflow } = useDeleteWorkflow(id);

  useEffect(() => {
    if (updatedWorkflow) {
      onChange?.(updatedWorkflow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedWorkflow]);

  useEffect(() => {
    if (revertedWorkflow) {
      onChange?.(revertedWorkflow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revertedWorkflow]);

  switch (status) {
    case 'draft':
      return (
        <Fragment>
          {version > 1 && (
            <Fragment>
              <Button
                variant='outlined'
                color='secondary'
                onClick={() => setOpenRevertDialog(true)}
              >
                Revert to Previous Version
              </Button>
              {openRevertDialog && (
                <ConfirmDialog
                  title={`Revert to version: ${version - 1}`}
                  message={`Are you sure you want to erase any changes in ${name} version: ${version} and rollback to version: ${
                    version - 1
                  }? You will not be able to undo this change.`}
                  open={openRevertDialog}
                  onCancel={() => {
                    setOpenRevertDialog(false);
                  }}
                  onConfirm={() => {
                    setOpenRevertDialog(false);
                    revert();
                  }}
                />
              )}
            </Fragment>
          )}
          <Tooltip
            title={
              workflow.taskType
                ? 'Cannot delete a workflow currently in use by an entity'
                : ''
            }
            placement='left'
            arrow
          >
            <div
              style={{
                marginRight: 10
              }}
            >
              <Button
                variant='contained'
                color='primary'
                disabled={workflow.taskType != null}
                onClick={() => setOpenConfirmDialog(true)}
              >
                Delete
              </Button>
            </div>
          </Tooltip>
          {openConfirmDialog && (
            <ConfirmDialog
              title={'Delete workflow'}
              message={
                'Are you sure you want to delete this workflow? This action cannot be undone.'
              }
              confirmLabel={'Confirm'}
              open={openConfirmDialog}
              onCancel={() => {
                setOpenConfirmDialog(false);
              }}
              onConfirm={() => {
                setOpenConfirmDialog(false);
                deleteWorkflow();
                history.goBack();
              }}
            />
          )}
          <Button
            variant='contained'
            color='primary'
            onClick={() => setOpenSignatureDialog(true)}
          >
            Publish
          </Button>
          {openSignatureDialog && (
            <ElectronicSignatureDialog
              open={openSignatureDialog}
              meaning='Sign and approve workflow.'
              onCancel={() => {
                setOpenSignatureDialog(false);
              }}
              onSigned={() => {
                setOpenSignatureDialog(false);
                updateWorkflow({ status: 'published' });
              }}
            />
          )}
        </Fragment>
      );
    case 'published':
      return (
        <Fragment>
          <Button
            variant='contained'
            color='primary'
            onClick={() => updateWorkflow({ status: 'draft' })}
          >
            Start New Version
          </Button>
        </Fragment>
      );
    default:
      return null;
  }
};

export default WorkflowStatusActions;
